import React from 'react';
import { PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { FileText, DollarSign, Calendar, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { KPICard, DataTable, formatCurrency, COLORS } from '../reportShared';

export default function SummaryTab({ summary }) {
  return (
    <div className="space-y-6" data-testid="reports-summary-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard title="Total Claims" value={summary.totalClaims?.toLocaleString() || 0} icon={FileText} color="blue" />
        <KPICard title="Total Amount" value={formatCurrency(summary.totalAmount)} icon={DollarSign} color="green" />
        <KPICard title="Pending" value={`${summary.pendingCount || 0} (${formatCurrency(summary.pendingAmount)})`} icon={Calendar} color="amber" />
        <KPICard title="Avg Claim" value={formatCurrency(summary.avgClaimAmount)} icon={TrendingUp} color="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Claims by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={summary.statusDistribution || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(summary.statusDistribution || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Daily Claims Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={summary.dailyTrend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#0088FE" fill="#0088FE" fillOpacity={0.3} name="Claims" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Recent Claims ({summary.filteredClaims?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: 'claim_id', header: 'Claim ID' },
              { key: 'patient_name', header: 'Patient' },
              { key: 'hospital_name', header: 'Hospital' },
              { key: 'total_claim_amount', header: 'Amount', align: 'right', render: (val) => formatCurrency(val) },
              {
                key: 'status',
                header: 'Status',
                render: (val) => (
                  <Badge variant={val?.toUpperCase() === 'PAID' ? 'success' : val?.toUpperCase() === 'REJECTED' ? 'destructive' : 'warning'}>
                    {val}
                  </Badge>
                )
              },
              { key: 'timestamp', header: 'Date', render: (val) => val?.split('T')[0] || val?.split(' ')[0] }
            ]}
            data={(summary.filteredClaims || []).slice(0, 20)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
